import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  crmCampaignsService,
  type CampaignSettings,
  type CampaignStatsQuery,
  type CampaignStep,
  type NewCampaign,
  type RecipientsQuery,
} from '@/services/crmCampaignsService'
import { whatsappService } from '@/services/whatsappService'
import { queryKeys } from '../keys'

// La barra de consentimiento avanza a unas 4 consultas por segundo: mirar cada
// 3 s basta para que se vea moverse sin cargar al servidor.
const CONSENT_POLL_MS = 3_000
const STATS_POLL_MS = 30_000

export function useCrmCampaignsQuery() {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.lists(),
    queryFn: () => crmCampaignsService.list(),
  })
}

export function useCrmCampaignQuery(id: string) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.detail(id),
    queryFn: () => crmCampaignsService.get(id),
  })
}

export function useCampaignRecipientsQuery(id: string, params: RecipientsQuery) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.recipients(id, params),
    queryFn: () => crmCampaignsService.listRecipients(id, params),
    placeholderData: keepPreviousData,
  })
}

/** En una campaña activa los números cambian solos: por eso `isActive`. */
export function useCampaignStatsQuery(id: string, params: CampaignStatsQuery, isActive: boolean) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.stats(id, params),
    queryFn: () => crmCampaignsService.stats(id, params),
    refetchInterval: isActive ? STATS_POLL_MS : false,
    placeholderData: keepPreviousData,
  })
}

export function useAudienceConsentQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.consent(id),
    queryFn: () => crmCampaignsService.getAudienceConsent(id),
    enabled,
    // Con `total = 0` todavía no hay audiencia: `pendiente` no significa «en marcha».
    refetchInterval: (query) => {
      const data = query.state.data
      return data && data.total > 0 && data.estado !== 'hecho' ? CONSENT_POLL_MS : false
    },
  })
}

export function useCampaignFilterOptionsQuery() {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.filterOptions(),
    queryFn: () => crmCampaignsService.filterOptions(),
  })
}

export function useCampaignSettingsQuery() {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.settings(),
    queryFn: () => crmCampaignsService.getSettings(),
  })
}

export function useConsentSummaryQuery(days = 30) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.consentSummary(days),
    queryFn: () => crmCampaignsService.consentSummary(days),
  })
}

export function useWabaTemplatesQuery(wabaId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.templates(wabaId ?? ''),
    queryFn: () => whatsappService.getTemplates(wabaId ?? ''),
    enabled: Boolean(wabaId),
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: NewCampaign) => crmCampaignsService.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.lists() }),
  })
}

export function useUpdateCampaign(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<NewCampaign>) => crmCampaignsService.update(id, data),
    onSuccess: (campaign) => {
      queryClient.setQueryData(queryKeys.crmCampaigns.detail(id), campaign)
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.lists() })
    },
  })
}

export function useReplaceCampaignSteps(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (steps: CampaignStep[]) => crmCampaignsService.replaceSteps(id, steps),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.detail(id) }),
  })
}

export function usePreviewAudience(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => crmCampaignsService.previewAudience(id),
    // Sustituye los destinatarios y vuelve a comprobar el consentimiento: lo
    // que hubiera en caché de la campaña ya no vale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.detail(id) }),
  })
}

export function useImportCampaignCsv(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => crmCampaignsService.importCsv(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.detail(id) }),
  })
}

export function useUpdateRecipients(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, included }: { ids: string[]; included: boolean }) =>
      crmCampaignsService.updateRecipients(id, ids, included),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.detail(id) }),
  })
}

export function useSimulateCampaign(id: string) {
  return useMutation({
    mutationFn: (startDate?: string) => crmCampaignsService.simulate(id, startDate),
  })
}

export function useTestSendCampaign(id: string) {
  return useMutation({
    mutationFn: ({ step, phone }: { step: number; phone: string }) =>
      crmCampaignsService.testSend(id, step, phone),
  })
}

export function useLaunchCampaign(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (startDate?: string) => crmCampaignsService.launch(id, startDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.all }),
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => crmCampaignsService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.all }),
  })
}

export function useOptOutReviewsQuery() {
  return useQuery({
    queryKey: queryKeys.crmCampaigns.optOutReviews(),
    queryFn: () => crmCampaignsService.listOptOutReviews(),
  })
}

export function useDecideOptOutReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: 'confirmar' | 'descartar' }) =>
      crmCampaignsService.decideOptOutReview(id, accion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.all }),
  })
}

export type CampaignTransition = 'pause' | 'resume' | 'archive'

export function useCampaignTransition(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (transition: CampaignTransition) => crmCampaignsService[transition](id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.crmCampaigns.all }),
  })
}

export function useUpdateCampaignSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CampaignSettings) => crmCampaignsService.updateSettings(data),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.crmCampaigns.settings(), settings)
    },
  })
}
